import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, ShieldCheck, GraduationCap, ScrollText, BookOpen, ImagePlus, DollarSign,
  Sparkles, Plus, Minus, X, Loader2, History, ArrowRight, Trash2,
  Download, Upload, PhoneCall,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import { usePager, Pager } from "@/components/ui/TablePager";
import { fmtDate } from "@/lib/dateUtils";
import { TeacherActivityModal } from "@/components/admin/TeacherActivityModal";
import { Activity, WifiOff } from "lucide-react";

const QUICK_ADD = [100, 500, 1000, 5000];

// Export teachers to Excel + bulk import from CSV/XLSX.
function TeacherImportExport({ onImported }) {
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const exportXlsx = async () => {
    try {
      const { data } = await api.get("/admin/teachers/export", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(data);
      a.download = "teachers.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error("Export failed"); }
  };
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/teachers/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.created} teacher(s)${data.skipped?.length ? ` · ${data.skipped.length} already existed` : ""} — default password ${data.default_password}, changed on first login`, { duration: 9000 });
      if (data.errors?.length) toast.error(`${data.errors.length} row(s) had problems: ${data.errors.slice(0, 3).join("; ")}`, { duration: 9000 });
      onImported?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };
  return (
    <>
      <button onClick={exportXlsx} data-testid="teachers-export-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5 rounded-md">
        <Download size={12} /> Export Excel
      </button>
      <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="teachers-import-btn"
        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5 rounded-md disabled:opacity-60">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Import
      </button>
      <input ref={fileRef} type="file" accept=".csv,.xlsx" className="hidden" onChange={onFile} data-testid="teachers-import-input" />
    </>
  );
}

// One-click "run a survey on this teacher" — picks the active survey (or asks).
function SurveyShortcut({ teacher }) {
  const nav = useNavigate();
  const go = async () => {
    try {
      const { data } = await api.get("/surveys");
      const active = (data.surveys || []).filter((s) => s.status === "active");
      if (!active.length) { toast.error("No active surveys — create one in Surveys first"); return; }
      if (active.length === 1) { nav(`/surveys/run/${active[0].id}?teacher=${teacher.id}`); return; }
      const pick = window.prompt(`Which survey?\n${active.map((s, i) => `${i + 1}. ${s.title}`).join("\n")}\n\nEnter a number:`, "1");
      const idx = parseInt(pick, 10) - 1;
      if (active[idx]) nav(`/surveys/run/${active[idx].id}?teacher=${teacher.id}`);
    } catch { toast.error("Could not load surveys"); }
  };
  return (
    <button onClick={go} data-testid={`admin-survey-${teacher.id}`}
      title="Run a quality survey on this teacher"
      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded-md">
      <PhoneCall size={12} /> Survey
    </button>
  );
}

// Admin setting — the "Video Help" guide URL shown to every teacher.
function HelpVideoSetting() {
  const [url, setUrl] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings/help-video").then(({ data }) => setUrl(data.url || "")).catch(() => {});
  }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/help-video", { url: url.trim() });
      toast.success(url.trim() ? "Help video URL updated — teachers see it immediately" : "Cleared — teachers see the default video");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid="help-video-setting">
      <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-1">/ help video</div>
      <div className="text-sm text-neutral-600 mb-3">
        The video shown in the <strong>Video Help</strong> modal (header button + first teacher login). Paste a Bunny.net embed or YouTube link. Leave empty to use the default video.
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://iframe.mediadelivery.net/embed/… or https://youtube.com/watch?v=…"
          data-testid="help-video-url-input"
          className="flex-1 min-w-[280px] bg-white border border-neutral-300 px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none"
        />
        <button
          onClick={save}
          disabled={saving}
          data-testid="help-video-save-btn"
          className="inline-flex items-center gap-1.5 px-5 py-2.5 bg-[#0A0C10] text-white text-sm font-bold rounded-md hover:bg-black disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : null} Save
        </button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [tab, setTab] = useState("teacher");
  const [q, setQ] = useState("");
  const [creditTarget, setCreditTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null);
  const [netAlerts, setNetAlerts] = useState([]);
  const [selected, setSelected] = useState([]);

  const loadUsers = async () => {
    const u = await api.get("/admin/users", { params: { role: tab, q } });
    setUsers((u.data.users || []).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
    setSelected([]);
  };

  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const allChecked = users.length > 0 && users.every((u) => selected.includes(u.id));
  const toggleAll = () => setSelected(allChecked ? [] : users.map((u) => u.id));
  const { page, setPage, pages, pageItems, total } = usePager(users, 25);

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Permanently delete ${selected.length} ${tab} account${selected.length > 1 ? "s" : ""}? This cannot be undone.`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/admin/users/${id}`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    if (ok) toast.success(`Deleted ${ok} account${ok > 1 ? "s" : ""}`);
    if (ok < selected.length) toast.error(`${selected.length - ok} could not be deleted`);
    loadUsers();
  };

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/admin/dashboard");
      setData(data);
    })();
    api.get("/admin/net-alerts").then((r) => setNetAlerts(r.data.alerts || [])).catch(() => {});
  }, []);

  useEffect(() => { loadUsers(); /* eslint-disable-next-line */ }, [tab]);

  if (!data) return <div className="text-neutral-500">Loading…</div>;
  const s = data.stats;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ admin · platform overview</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter inline-flex items-center gap-2"><ShieldCheck size={26} /> Super Admin</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/packages"
            data-testid="admin-packages-link"
            className="text-xs font-bold border border-black/15 text-[#0A0C10] px-4 py-2.5 rounded-md hover:border-black inline-flex items-center gap-1.5"
          >
            <DollarSign size={13} /> Packages &amp; credits <ArrowRight size={12} />
          </Link>
          <Link
            to="/admin/usage"
            data-testid="admin-usage-link"
            className="text-xs font-bold bg-[#0A0C10] text-white px-4 py-2.5 rounded-md hover:bg-black inline-flex items-center gap-1.5"
          >
            <History size={13} /> Usage &amp; cost analytics <ArrowRight size={12} />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
        <Stat label="Teachers" value={s.teachers} icon={Users} />
        <Stat label="Students" value={s.students} icon={GraduationCap} />
        <Stat label="Classes" value={s.classes} icon={Users} />
        <Stat label="Revenue" value={`$${s.total_revenue.toFixed(2)}`} icon={DollarSign} accent />
        <Stat label="AI quizzes" value={s.quizzes} icon={ScrollText} />
        <Stat label="Lesson plans" value={s.lesson_plans} icon={BookOpen} />
        <Stat label="Generated content" value={s.generated_content} icon={ImagePlus} />
        <Stat label="Paid transactions" value={s.paid_transactions} icon={DollarSign} />
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-3">/ plans distribution</div>
        <div className="grid sm:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
          {data.plans_distribution.map((p) => (
            <div key={p.plan} className="bg-white p-4">
              <div className="text-xs uppercase font-mono tracking-[0.18em] text-neutral-500">{p.plan}</div>
              <div className="text-2xl font-display font-bold">{p.count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Help video — admin-configurable URL for the header "Video Help" modal */}
      <HelpVideoSetting />

      {/* Connection quality alerts — teachers with repeatedly poor internet */}
      {netAlerts.length > 0 && (
        <div data-testid="net-alerts-panel" className="border border-red-300 bg-red-50/60 rounded-md overflow-hidden">
          <div className="px-5 py-3 border-b border-red-200 flex items-center gap-2">
            <WifiOff size={15} className="text-red-500" />
            <div className="text-sm font-bold">Connection quality alerts</div>
            <div className="text-xs text-neutral-500">— teachers with repeatedly poor internet in the last 30 days. Consider reaching out.</div>
          </div>
          <div className="divide-y divide-red-100">
            {netAlerts.map((a) => (
              <div key={a.user_id} className="px-5 py-3 flex flex-wrap items-center gap-x-4 gap-y-1.5" data-testid={`net-alert-${a.user_id}`}>
                <div className="min-w-[180px]">
                  <div className="font-semibold text-sm">{a.full_name || a.email}</div>
                  <div className="text-xs text-neutral-500">{a.email}</div>
                </div>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded border bg-red-100 text-red-700 border-red-300">
                  <WifiOff size={10} /> {Math.round(a.poor_ratio * 100)}% poor samples
                </span>
                <span className="text-xs text-neutral-600 font-mono">avg {a.avg_rtt_ms ?? "?"} ms · {a.avg_downlink_mbps ?? "?"} Mbps</span>
                <span className="text-xs text-neutral-500">{a.classes_affected} class{a.classes_affected === 1 ? "" : "es"} affected</span>
                <span className="text-xs text-neutral-400 font-mono">last seen {fmtDate(a.last_seen)}</span>
                <button
                  onClick={() => setActivityTarget({ id: a.user_id, full_name: a.full_name, email: a.email, plan: a.plan, created_at: a.created_at })}
                  data-testid={`net-alert-activity-${a.user_id}`}
                  className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold border border-red-300 text-red-700 hover:bg-red-100 px-3 py-1.5 rounded-md"
                >
                  <Activity size={12} /> View activity
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-neutral-200">
        <div className="flex flex-wrap items-center border-b border-neutral-200">
          <div className="flex">
            {["teacher", "student", "parent"].map((r) => (
              <button
                key={r}
                onClick={() => setTab(r)}
                className={`px-5 py-3 text-sm font-medium capitalize transition-colors ${tab === r ? "bg-[#0A0C10] text-white" : "text-neutral-600 hover:text-[#0A0C10]"}`}
                data-testid={`admin-tab-${r}`}
              >
                {r}s
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2 pr-4 py-2 flex-wrap">
            {tab === "teacher" && <TeacherImportExport onImported={loadUsers} />}
            {selected.length > 0 && (
              <button onClick={bulkDelete} data-testid="admin-users-bulk-delete"
                className="inline-flex items-center gap-1.5 text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md">
                <Trash2 size={12} /> Delete selected ({selected.length})
              </button>
            )}
            <form onSubmit={(e) => { e.preventDefault(); loadUsers(); }}>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onBlur={loadUsers}
                placeholder="Search name or email…"
                data-testid="admin-user-search"
                className="text-sm px-3 py-1.5 rounded-md border border-neutral-200 focus:border-[#0A0C10] outline-none w-36 sm:w-64"
              />
            </form>
          </div>
        </div>

        <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              <th className="px-5 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="admin-users-select-all" /></th>
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Email</th>
              <th className="px-5 py-3">Phone</th>
              <th className="px-5 py-3">Plan</th>
              <th className="px-5 py-3">Credits</th>
              <th className="px-5 py-3 whitespace-nowrap">Joined</th>
              <th className="px-5 py-3 w-1 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr><td colSpan={7} className="px-5 py-8 text-center text-neutral-500">No users.</td></tr>
            ) : pageItems.map((u) => (
              <tr key={u.id} className="border-t border-neutral-100 hover:bg-neutral-50/60" data-testid={`admin-user-row-${u.id}`}>
                <td className="px-5 py-3"><input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`admin-user-check-${u.id}`} /></td>
                <td className="px-5 py-3 font-medium">{u.full_name || <span className="text-neutral-400">—</span>}</td>
                <td className="px-5 py-3 text-neutral-600">{u.email}</td>
                <td className="px-5 py-3 font-mono text-xs text-neutral-600 whitespace-nowrap" data-testid={`admin-phone-${u.id}`}>{u.phone || <span className="text-neutral-300">—</span>}</td>
                <td className="px-5 py-3 capitalize text-neutral-600">{u.plan || "trial"}</td>
                <td className="px-5 py-3">
                  <span className="inline-flex items-center gap-1.5 font-mono font-semibold text-[#0A0C10]" data-testid={`admin-credits-${u.id}`}>
                    <Sparkles size={12} className="text-[#FFB400]" /> {u.ai_credits ?? 0}
                  </span>
                </td>
                <td className="px-5 py-3 font-mono text-xs text-neutral-600 whitespace-nowrap" data-testid={`admin-joined-${u.id}`}>{fmtDate(u.created_at)}</td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-1.5">
                    {u.role === "teacher" && (
                      <button
                        onClick={() => setActivityTarget(u)}
                        data-testid={`admin-activity-${u.id}`}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#0EA5E9]/40 text-[#0369A1] hover:bg-[#E0F2FE] px-3 py-1.5 rounded-md"
                        title="Complete day-wise activity dashboard for this teacher"
                      >
                        <Activity size={12} /> Activity
                      </button>
                    )}
                    {u.role === "teacher" && <SurveyShortcut teacher={u} />}
                    <button
                      onClick={() => setCreditTarget(u)}
                      data-testid={`admin-adjust-credits-${u.id}`}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] text-neutral-700 hover:text-[#0A0C10] px-3 py-1.5 rounded-md"
                    >
                      <Plus size={12} /> Credits
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <Pager page={page} pages={pages} total={total} onPage={setPage} testid="admin-users-pager" />
      </div>

      <TeacherActivityModal user={activityTarget} onClose={() => setActivityTarget(null)} />

      <CreditModal
        user={creditTarget}
        onClose={() => setCreditTarget(null)}
        onSaved={(newBalance) => {
          setUsers((list) => list.map((u) => u.id === creditTarget.id ? { ...u, ai_credits: newBalance } : u));
          setCreditTarget(null);
        }}
        onLimitSaved={(n) => {
          setUsers((list) => list.map((u) => u.id === creditTarget.id ? { ...u, class_limit: n } : u));
        }}
      />
    </div>
  );
}

function CreditModal({ user, onClose, onSaved, onLimitSaved }) {
  const [delta, setDelta] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [classLimit, setClassLimit] = useState("");

  const saveClassLimit = async () => {
    const n = Number(classLimit);
    if (!Number.isInteger(n) || n < 1) { toast.error("Enter a whole number of at least 1"); return; }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { class_limit: n });
      toast.success(`Class limit set to ${n} for ${user.full_name || user.email}`);
      onLimitSaved?.(n);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    setDelta(""); setNote(""); setHistory([]); setClassLimit("");
    if (!user) return;
    setLoadingHistory(true);
    api.get(`/admin/users/${user.id}/credits/history`)
      .then((r) => setHistory(r.data.history || []))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [user]);

  if (!user) return null;

  const submit = async (deltaValue) => {
    const n = Number(deltaValue);
    if (!Number.isInteger(n) || n === 0) { toast.error("Enter a non-zero whole number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/credits`, { delta: n, note });
      toast.success(`Credits ${n > 0 ? "added" : "removed"} — new balance ${data.balance}`);
      onSaved(data.balance);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="credit-modal">
      <div className="bg-white w-full max-w-xl rounded-lg border themed-border shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 pb-4 border-b themed-border">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-neutral-500">/ credits · manual adjust</div>
            <h3 className="font-display font-bold text-xl tracking-tighter mt-1">{user.full_name || user.email}</h3>
            <div className="text-xs text-neutral-500 mt-0.5">{user.email} · {user.role} · Current balance: <strong className="text-[#0A0C10]">{user.ai_credits ?? 0}</strong></div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-[#0A0C10]"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-5">
          {/* Quick presets */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">Quick grant</div>
            <div className="flex flex-wrap gap-2">
              {QUICK_ADD.map((n) => (
                <button
                  key={n}
                  onClick={() => submit(n)}
                  disabled={busy}
                  data-testid={`credit-quick-${n}`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-neutral-200 hover:border-[#0A0C10] px-3 py-2 rounded-md disabled:opacity-50"
                >
                  <Plus size={12} /> {n}
                </button>
              ))}
            </div>
          </div>

          {/* Manual amount */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Custom amount</div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
                placeholder="e.g. 250 or -50"
                data-testid="credit-delta"
                className="flex-1 px-3 py-2 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10]"
              />
              <button
                onClick={() => submit(delta)}
                disabled={busy || !delta}
                data-testid="credit-submit"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium disabled:opacity-50 hover:bg-[#0A0C10]/90"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : Number(delta) < 0 ? <Minus size={14} /> : <Plus size={14} />}
                Apply
              </button>
            </div>
            <div className="text-[11px] text-neutral-500 mt-1.5">Positive to grant, negative to revoke. Balance is floored at 0.</div>
          </div>

          {/* Scheduled class limit override */}
          {user.role === "teacher" && (
            <div className="border border-neutral-200 rounded-md p-4 bg-[#FBFAF7]">
              <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Scheduled class limit</div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  value={classLimit}
                  onChange={(e) => setClassLimit(e.target.value)}
                  placeholder={`Current: ${user.class_limit ?? 20}`}
                  data-testid="class-limit-input"
                  className="flex-1 px-3 py-2 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10] bg-white"
                />
                <button
                  onClick={saveClassLimit}
                  disabled={busy || !classLimit}
                  data-testid="class-limit-save"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#0A0C10] text-[#FBFAF7] text-sm font-medium disabled:opacity-50 hover:bg-[#0A0C10]/90"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Set limit
                </button>
              </div>
              <div className="text-[11px] text-neutral-500 mt-1.5">
                How many classes this teacher can schedule in total (plan default is 20). Current: <strong>{user.class_limit ?? 20}</strong>
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-1.5">Note (optional)</div>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. Promo grant, refund, correction…"
              data-testid="credit-note"
              className="w-full px-3 py-2 rounded-md border themed-border text-sm outline-none focus:border-[#0A0C10]"
              maxLength={280}
            />
          </div>

          {/* History */}
          <div>
            <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-neutral-500 mb-2">
              <History size={11} /> Recent adjustments
            </div>
            {loadingHistory ? (
              <div className="text-xs text-neutral-400 py-4 text-center">Loading…</div>
            ) : history.length === 0 ? (
              <div className="text-xs text-neutral-400 py-4 text-center border border-dashed border-neutral-200 rounded-md">No manual adjustments yet.</div>
            ) : (
              <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-md max-h-56 overflow-y-auto" data-testid="credit-history">
                {history.map((h, i) => (
                  <li key={i} className="flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className={`font-mono font-semibold ${h.delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                          {h.delta > 0 ? `+${h.delta}` : h.delta}
                        </span>
                        <ArrowRight size={11} className="text-neutral-400" />
                        <span className="font-mono text-neutral-700">{h.balance_after}</span>
                        {h.note && <span className="text-neutral-500 truncate ml-2">— {h.note}</span>}
                      </div>
                      <div className="text-[10px] text-neutral-400 mt-0.5">{(h.at || "").replace("T", " ").slice(0, 16)} · by {h.admin_email}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-neutral-500">{label}</div>
        <Icon size={16} className={accent ? "text-[#FFD600]" : "text-neutral-400"} />
      </div>
      <div className="font-display font-bold text-3xl mt-2">{value}</div>
    </div>
  );
}
